import { useEffect, useMemo, useState, useRef, useLayoutEffect } from "react";
import "./MemoryGame.css";
import { host } from "../globalVariables";
import { Badge, Button, Form } from "react-bootstrap";
import useMemoryMultiplayer from "./useMemoryMultiplayer";

const KARTE_MIN_BREITE = 80; // px
const KARTE_MAX_BREITE = 150; // px
const KARTE_SEITENVERHAELTNIS = 4 / 3; // Höhe = Breite * 4/3 (passt zum CSS aspect-ratio: 3/4)
const GRID_LUECKE = 10; // px – muss zum gap im CSS passen (siehe Punkt 5)
const STORAGE_KEY_PREFIX = "mg-mitarbeitermemo-state-";
const BEISPIEL_SEED = 1; // fester Seed -> der "Beispiel"-Button erzeugt immer dasselbe Deck

// Deterministischer Pseudo-Zufallsgenerator (mulberry32).
// Gleicher seed -> exakt gleiche Zahlenfolge -> exakt gleiches Deck.
function erzeugeSeededRandom(seed) {
   let t = seed >>> 0;
   return function () {
      t += 0x6d2b79f5;
      let r = Math.imul(t ^ (t >>> 15), 1 | t);
      r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
   };
}

// Fisher-Yates Shuffle – nimmt optional eine eigene Zufallsfunktion
// (z. B. einen seeded random) statt immer Math.random zu verwenden.
function shuffle(array, random = Math.random) {
   const a = [...array];
   for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
   }
   return a;
}

function waehleZufaelligeMitarbeiter(liste, anzahl, random = Math.random) {
   // Fisher-Yates statt sort(() => random() - 0.5) – Letzteres ist kein
   // korrekter Shuffle und würde bei einem seeded random erst recht
   // verzerrte/nicht reproduzierbare Ergebnisse liefern.
   return shuffle(liste, random).slice(0, anzahl);
}

function buildDeck(mitarbeiter, random = Math.random) {
   const cards = mitarbeiter.flatMap((m) => [
      { uid: `${m.id}-foto`, employeeId: m.id, type: "foto", data: m },
      { uid: `${m.id}-info`, employeeId: m.id, type: "info", data: m },
   ]);
   return shuffle(cards, random);
}

function formatiereZeit(gesamtSekunden) {
   const minuten = Math.floor(gesamtSekunden / 60)
      .toString()
      .padStart(2, "0");
   // const rest = (gesamtSekunden % 60).toString().padStart(2, "0")
   const rest = (gesamtSekunden % 60).toFixed(3).padStart(6, "0");
   return `${minuten}:${rest}`;
}

function getStorageKey(user) {
   return `${STORAGE_KEY_PREFIX}${user?.username || "anon"}`;
}

function ladeGespeichertenZustand(user) {
   try {
      const raw = window.localStorage.getItem(getStorageKey(user));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
   } catch (e) {
      console.warn("Konnte gespeicherten Memo-Zustand nicht laden:", e);
      return {};
   }
}

export default function MemoryGame({
   mitarbeiter,
   anzahl = 9,
   updateUserData,
   updateStartTimeData,
   setAnzahl = () => {},
   photoBaseUrl = "/api/mitarbeiterfotos",
   onWin,
   user,
   beispielSeed = null, // Zahl -> initiales Deck ist reproduzierbar; null -> echt zufällig
   multiplayer = false, // true -> Zustand kommt komplett vom Socket-Server
   multiplayerUrl = host, // der normale Backend-Host, Socket.IO läuft dort mit
}) {
   // Wird nur beim allerersten Rendern befüllt und danach nicht mehr verändert
   const gespeichertRef = useRef(null);
   if (gespeichertRef.current === null) {
      gespeichertRef.current = ladeGespeichertenZustand(user);
   }
   const gespeichert = gespeichertRef.current;
   const [isEnabled, setIsEnabled] = useState(true);
   const [hardcore, setHardcore] = useState(gespeichert.hardcore ?? false);
   const [timerAktiv, setTimerAktiv] = useState(gespeichert.timerAktiv ?? false);
   const [sekunden, setSekunden] = useState(() => {
      if (gespeichert.timerAktiv && gespeichert.startZeit) {
         // Zeit seit dem letzten Speichern korrekt weiterlaufen lassen
         return Math.floor((Date.now() - gespeichert.startZeit) / 1000);
      }
      return gespeichert.sekunden ?? 0;
   });
   const [deck, setDeck] = useState(() => {
      if (beispielSeed != null) {
         const random = erzeugeSeededRandom(beispielSeed);
         return buildDeck(waehleZufaelligeMitarbeiter(mitarbeiter, anzahl, random), random);
      }
      if (Array.isArray(gespeichert.deck) && gespeichert.deck.length) {
         return gespeichert.deck;
      }
      return buildDeck(waehleZufaelligeMitarbeiter(mitarbeiter, anzahl));
   });
   const [flipped, setFlipped] = useState(gespeichert.flipped ?? []); // uids der aktuell aufgedeckten Karten (max 2)
   const [matched, setMatched] = useState(() => new Set(gespeichert.matched ?? [])); // employeeIds, die gelöst sind
   const [versuche, setVersuche] = useState(gespeichert.versuche ?? 0);
   const [locked, setLocked] = useState(false); // nach Reload nie gesperrt starten
   const startZeitRef = useRef(gespeichert.startZeit ?? null);
   const gridRef = useRef(null);
   const outerRef = useRef(null); // der scrollbare Wrapper mit fester Höhe
   const [kartenBreite, setKartenBreite] = useState(KARTE_MAX_BREITE);
   const [spalten, setSpalten] = useState(1);
   // Eingabe für den Beispiel-Seed – als String, damit das Feld beim Tippen
   // (z. B. leer oder "-") nicht sofort auf 0/NaN zurückspringt.
   const [beispielSeedEingabe, setBeispielSeedEingabe] = useState(String(BEISPIEL_SEED));

   // --- Multiplayer -------------------------------------------------------
   // Im Multiplayer ist der Server die einzige Wahrheit: Deck, aufgedeckte
   // Karten, gefundene Paare und "wer ist am Zug" kommen von dort. Der lokale
   // State oben wird dann nicht benutzt (und auch nicht gespeichert).
   const mp = useMemoryMultiplayer({
      aktiv: multiplayer,
      name: user?.username,
      serverUrl: multiplayerUrl,
   });
   const mpAktiv = multiplayer && !!mp.zustand;
   const binAmZug = mpAktiv && mp.zustand.amZug === user?.username;

   const aktDeck = mpAktiv ? mp.zustand.deck : deck;
   const aktFlipped = mpAktiv ? mp.zustand.flipped : flipped;
   const aktMatched = useMemo(
      () => (mpAktiv ? new Set(mp.zustand.matched) : matched),
      [mpAktiv, mp.zustand?.matched, matched],
   );
   const aktVersuche = mpAktiv ? mp.zustand.versuche : versuche;
   const aktAnzahl = mpAktiv ? mp.zustand.anzahl : anzahl;
   const gewonnen = mpAktiv ? mp.zustand.gewonnen : matched.size === anzahl;

   // Falls der gespeicherte Zustand zu einer anderen Kartenanzahl gehört
   // als der Elternkomponente bekannt ist, den Elternstate einmalig angleichen.
   useEffect(() => {
      if (gespeichert.anzahl && gespeichert.anzahl !== anzahl) {
         setAnzahl(gespeichert.anzahl);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // HINWEIS: Das frühere sekündliche Ticken lief hier über setSekunden im
   // Parent -> löste alle 250ms ein Rerender der GESAMTEN Komponente aus,
   // inkl. Toolbar und Seed-Eingabefeld (dadurch das sichtbare Flackern).
   // Das Ticken übernimmt jetzt ausschließlich <StoppuhrAnzeige> mit
   // eigenem lokalem State - siehe unten. `sekunden` hier wird nur noch für
   // den finalen/gespeicherten Wert (Laden, Sieg, Reset) gebraucht.

   // Aktuellen Spielstand bei jeder relevanten Änderung persistieren.
   // Im Multiplayer wird nichts gespeichert - der Server hält den Zustand.
   useEffect(() => {
      // setIsEnabled([12, 17, 18, 19, 20, 21, 22, 23, 24].includes(new Date().getHours()));

      if (multiplayer) return;
      try {
         const zustand = {
            hardcore,
            deck,
            flipped,
            matched: Array.from(matched),
            versuche,
            sekunden,
            timerAktiv,
            startZeit: startZeitRef.current,
            anzahl,
         };
         window.localStorage.setItem(getStorageKey(user), JSON.stringify(zustand));
      } catch (e) {
         console.warn("Konnte Memo-Zustand nicht speichern:", e);
      }
   }, [hardcore, deck, flipped, matched, versuche, sekunden, timerAktiv, anzahl, user, multiplayer]);

   useLayoutEffect(() => {
      function berechneLayout() {
         const el = gridRef.current;
         const outer = outerRef.current;
         if (!el || !outer) return;

         // WICHTIG: nicht window.innerHeight - getBoundingClientRect().top verwenden!
         // Das hängt von der Scroll-Position der ganzen Seite ab. Da oberhalb des
         // Memory-Spiels viel Inhalt steht (Spielregeln-Alert + Rangliste-Tabelle),
         // steht das Grid beim allerersten Laden (Scroll-Position 0) oft noch
         // unterhalb des sichtbaren Bereichs -> "top" > innerHeight -> negative
         // "verfügbare Höhe" -> Layout kollabiert auf 1 Spalte. Der outer-Container
         // hat dagegen eine FESTE Höhe (calc(100vh - 80px)), egal wie die Seite
         // gescrollt ist oder wo das Grid auf der Seite steht.
         const verfuegbareBreite = outer.clientWidth;
         const verfuegbareHoehe = outer.clientHeight - 16;

         if (verfuegbareHoehe <= 0 || verfuegbareBreite <= 0) return;

         const anzahlKarten = aktDeck.length;
         let besteBreite = 0;
         let besteSpalten = 1;

         for (let spalten = 1; spalten <= anzahlKarten; spalten++) {
            const zeilen = Math.ceil(anzahlKarten / spalten);
            const breiteAusBreite = (verfuegbareBreite - (spalten - 1) * GRID_LUECKE) / spalten;
            const breiteAusHoehe = (verfuegbareHoehe - (zeilen - 1) * GRID_LUECKE) / zeilen / KARTE_SEITENVERHAELTNIS;
            const moeglicheBreite = Math.min(breiteAusBreite, breiteAusHoehe);
            if (moeglicheBreite > besteBreite) {
               besteBreite = moeglicheBreite;
               besteSpalten = spalten;
            }
         }

         const geklemmteBreite = Math.min(KARTE_MAX_BREITE, Math.max(KARTE_MIN_BREITE, besteBreite));
         setKartenBreite(geklemmteBreite);
         setSpalten(besteSpalten);
      }

      berechneLayout();

      // Nur noch den outer-Container beobachten - das ist der einzige Wert,
      // von dem die Berechnung jetzt abhängt (kein Header, kein window-resize
      // auf Basis der Scroll-Position mehr nötig).
      const outer = outerRef.current;
      const ro = new ResizeObserver(() => berechneLayout());
      if (outer) ro.observe(outer);

      // Fallback: falls die erste Messung trotzdem zu früh war
      // (z.B. Fonts laden asynchron nach und ändern noch mal minimal die
      // Größe des Containers, ohne dass ResizeObserver das mitbekommt)
      const t1 = setTimeout(berechneLayout, 150);
      const t2 = setTimeout(berechneLayout, 500);

      return () => {
         ro.disconnect();
         clearTimeout(t1);
         clearTimeout(t2);
      };
   }, [aktDeck.length]);

   useEffect(() => {
      // Im Multiplayer wird weder Zeit gemessen noch etwas zum Server/zur DB
      // geschickt - der Sieg ist nur Anzeige.
      if (multiplayer) return;

      if (timerAktiv && gewonnen && (anzahl === 16 || anzahl === 4 || anzahl === mitarbeiter.length - 1)) {
         const vergangeneS = (Date.now() - startZeitRef.current) / 1000;
         setSekunden(vergangeneS);
         updateUserData({
            zeit: vergangeneS,
            versuche: versuche,
            t: new Date(),
            anzahl: anzahl,
            t_s: startZeitRef.current,
         });
      }

      if (gewonnen && onWin) onWin(versuche);
      if (gewonnen) setTimerAktiv(false);
   }, [gewonnen]); // eslint-disable-line react-hooks/exhaustive-deps

   const neuStarten = (anzahl, seed = null) => {
      const random = seed != null ? erzeugeSeededRandom(seed) : Math.random;
      const neuesDeck = buildDeck(waehleZufaelligeMitarbeiter(mitarbeiter, anzahl, random), random);

      if (multiplayer) {
         // Das Deck wird hier gemischt und an den Server geschickt; von dort
         // bekommen es alle Mitspieler. Lokal wird nichts gesetzt.
         mp.starten(neuesDeck);
         setAnzahl(anzahl);
         return;
      }

      setDeck(neuesDeck);

      setAnzahl(anzahl);
      setFlipped([]);
      setMatched(new Set());
      setVersuche(0);
      setLocked(false);
      setSekunden(0);
      setTimerAktiv(false);
      startZeitRef.current = null;
   };

   const handleClick = (card) => {
      if (multiplayer) {
         // Der Server prüft, ob der Klick erlaubt ist (am Zug, Karte frei,
         // keine Auswertung aktiv) - hier nur die offensichtlichen Fälle.
         if (!mpAktiv || !binAmZug || mp.zustand.gesperrt) return;
         if (aktFlipped.includes(card.uid) || aktMatched.has(card.employeeId)) return;
         mp.aufdecken(card.uid);
         return;
      }

      if (locked) return;
      if (flipped.includes(card.uid)) return;
      if (matched.has(card.employeeId) && flipped.length < 2) return;

      if (!timerAktiv) {
         if (startZeitRef.current == null) startZeitRef.current = Date.now();
         if (anzahl === 4 || anzahl === 16 || anzahl === mitarbeiter.length - 1) {
            updateStartTimeData(anzahl);
         }
         // startZeitRef.current = Date.now();
         setTimerAktiv(true);
      }

      const naechsteFlipped = [...flipped, card.uid];
      setFlipped(naechsteFlipped);

      if (naechsteFlipped.length >= 2) {
         setLocked(true);
         setVersuche((v) => v + 1);

         const [ersteUid, zweiteUid] = naechsteFlipped;
         const erste = deck.find((c) => c.uid === ersteUid);
         const zweite = deck.find((c) => c.uid === zweiteUid);

         const istPaar = erste.employeeId === zweite.employeeId && erste.type !== zweite.type;

         window.setTimeout(
            () => {
               if (istPaar) {
                  setMatched((prev) => new Set(prev).add(erste.employeeId));
               }
               setFlipped([]);
               setLocked(false);
            },
            istPaar ? 500 : 900,
         );
      }
   };
/* if(!isEnabled)
   return(<h5>Das Memory ist zwischen 12-13 Uhr und ab 17 Uhr aktiv</h5>) */

   if (isEnabled)
      return (
         <div className="mg-wrapper">
            <div className="mg-header">
               <h2 className="mg-title">Mitarbeiter-Memo{multiplayer ? " – Multiplayer" : ""}</h2>
               {multiplayer ? (
                  <MultiplayerLeiste
                     mp={mp}
                     mpAktiv={mpAktiv}
                     binAmZug={binAmZug}
                     eigenerName={user?.username}
                     versuche={aktVersuche}
                     geloest={aktMatched.size}
                     anzahl={aktAnzahl}
                     onStart={(n) => neuStarten(n)}
                     mitarbeiterAnzahl={mitarbeiter.length}
                  />
               ) : (
                  <div className="mg-stats">
                     <span>Schwierigkeitsgrad:</span>
                     {import.meta.env.DEV && (
                        <Button
                           variant="outline-dark"
                           disabled={timerAktiv}
                           onClick={() => {
                              setHardcore(false);
                              neuStarten(4);
                           }}
                        >
                           Test
                        </Button>
                     )}
                     <Button
                        variant="outline-dark"
                        disabled={timerAktiv}
                        onClick={() => {
                           setHardcore(false);
                           neuStarten(9);
                        }}
                     >
                        Trainingslevel
                     </Button>
                     {
                        <Button
                           variant="outline-dark"
                           disabled={
                              /* !user.username || user.username !== "JonathanMischke" && */
                              (new Date() < new Date("2026-09-18T05:58:00Z") &&
                                 mitarbeiter.filter((item) => item.Name === user.username)[0]?.ranking
                                    ?.mitarbeitermemo) ||
                              new Date() > new Date("2026-09-20T18:00:00Z") /* ||
                               */
                           }
                           onClick={() => {
                              setHardcore(false);
                              neuStarten(16);
                           }}
                        >
                           Qualifikation
                        </Button>
                     }
                     {/* <Button
                  variant="outline-dark"
                  disabled={false}
                  onClick={() => {
                     neuStarten(mitarbeiter.length - 1);
                     setHardcore(false);
                  }}
               >
                  Finale
               </Button> */}
                     <Button
                        variant="outline-dark"
                        disabled={false}
                        onClick={() => {
                           const seedZahl = Number(beispielSeedEingabe);
                           const seed = Number.isFinite(seedZahl) ? seedZahl : BEISPIEL_SEED;
                           // neuStarten(5, seed);
                           neuStarten(mitarbeiter.length - 1, seed);
                           setHardcore(false);
                        }}
                     >
                        Finale
                     </Button>
                     <Form.Control
                        type="number"
                        value={beispielSeedEingabe}
                        onChange={(e) => setBeispielSeedEingabe(e.target.value)}
                        placeholder="Seed"
                        aria-label="Seed für das Beispieldeck"
                        title="Seed für das Beispieldeck"
                        // Explizite Styles, falls globales CSS (z.B. in MemoryGame.css)
                        // pointer-events, Text- oder Hintergrundfarbe für Inputs überschreibt
                        // und das Feld dadurch unbedienbar/unsichtbar macht.
                        style={{
                           width: "90px",
                           display: "inline-block",
                           pointerEvents: "auto",
                           userSelect: "text",
                           backgroundColor: "#fff",
                           color: "#000",
                           position: "relative",
                           zIndex: 1,
                        }}
                     />
                     <Button
                        variant="outline-dark"
                        disabled={timerAktiv}
                        onClick={() => {
                           neuStarten(mitarbeiter.length - 1);
                           setHardcore(true);
                        }}
                     >
                        Fast unmöglich
                     </Button>
                     <StoppuhrAnzeige
                        timerAktiv={timerAktiv}
                        gewonnen={gewonnen}
                        startZeitRef={startZeitRef}
                        sekunden={sekunden}
                     />
                     <span className="mg-versuche">
                        Versuche: <strong>{versuche}</strong>
                     </span>
                     <span className="mg-fortschritt">
                        {matched.size} / {anzahl} Paare
                     </span>

                     {/*  <Button
                  disabled={
                     anzahl === 16
                     // && mitarbeiter.filter((item) => item.Name === user.username)[0]?.ranking?.mitarbeitermemo
                  }
                  variant="dark"
                  onClick={() => {
                     neuStarten(anzahl);
                  }}
               >
                  Neu starten
               </Button> */}
                  </div>
               )}
            </div>

            {gewonnen &&
               (multiplayer ? (
                  <div className="mg-win-banner">Fertig! {gewinnerText(mp.zustand?.spieler)}</div>
               ) : (
                  <div className="mg-win-banner">
                     Geschafft! Du hast alle Paare in {versuche} Versuchen und {sekunden} Sekunden gefunden.
                  </div>
               ))}

            <div
               ref={outerRef}
               style={{
                  overflow: "auto",
                  width: "100%",
                  height: "calc(100vh - 80px)",
                  padding: "0px",
               }}
            >
               <div
                  ref={gridRef}
                  className="mg-grid"
                  style={{
                     gridTemplateColumns: `repeat(${spalten}, ${kartenBreite}px)`,
                     // justifyContent: "center",
                  }}
               >
                  {aktDeck.map((card) => {
                     const istAufgedeckt = aktFlipped.includes(card.uid) || aktMatched.has(card.employeeId);
                     return (
                        <Karte
                           hardcore={hardcore}
                           key={card.uid}
                           card={card}
                           aufgedeckt={istAufgedeckt}
                           gelöst={aktMatched.has(card.employeeId)}
                           photoBaseUrl={photoBaseUrl}
                           // Im Multiplayer darf nur klicken, wer am Zug ist.
                           gesperrt={multiplayer && !binAmZug}
                           onClick={() => handleClick(card)}
                        />
                     );
                  })}
               </div>
            </div>
         </div>
      );
}

// Eigene Komponente mit eigenem lokalem State fürs Ticken. Dadurch löst der
// 250ms-Tick nur ein Rerender DIESER kleinen Anzeige aus - nicht mehr der
// gesamten Toolbar (Buttons, Seed-Eingabefeld etc.) im Elternteil. Das war
// die Ursache für das Flackern des Eingabefelds.
function StoppuhrAnzeige({ timerAktiv, gewonnen, startZeitRef, sekunden }) {
   const [angezeigteSekunden, setAngezeigteSekunden] = useState(sekunden);

   useEffect(() => {
      if (!timerAktiv || gewonnen) {
         // Pausiert/fertig/frisch geladen -> den vom Parent übergebenen
         // (finalen bzw. gespeicherten) Wert unverändert anzeigen.
         setAngezeigteSekunden(sekunden);
         return;
      }

      const intervalId = setInterval(() => {
         setAngezeigteSekunden((Date.now() - startZeitRef.current) / 1000);
      }, 250); // öfter prüfen als anzeigen -> Anzeige wirkt trotzdem exakt zur Sekunde

      return () => clearInterval(intervalId);
   }, [timerAktiv, gewonnen, sekunden, startZeitRef]);

   return (
      <span className="mg-timer">
         Zeit: <strong>{formatiereZeit(angezeigteSekunden)}</strong>
      </span>
   );
}

// Wer hat die meisten Paare? Bei Gleichstand gibt es mehrere Gewinner.
function gewinnerText(spieler) {
   if (!spieler?.length) return "";
   const max = Math.max(...spieler.map((s) => s.punkte));
   const gewinner = spieler.filter((s) => s.punkte === max);
   if (gewinner.length === 1) return `${gewinner[0].name} gewinnt mit ${max} Paaren.`;
   return `Unentschieden zwischen ${gewinner.map((s) => s.name).join(", ")} mit je ${max} Paaren.`;
}

// Toolbar für den Multiplayer-Modus: Verbindungsstatus, Punktestand,
// wer gerade am Zug ist und die Buttons zum Starten einer neuen Runde.
// Bewusst ohne Stoppuhr - im Multiplayer wird keine Zeit gemessen.
function MultiplayerLeiste({
   mp,
   mpAktiv,
   binAmZug,
   eigenerName,
   versuche,
   geloest,
   anzahl,
   onStart,
   mitarbeiterAnzahl,
}) {
   const spieler = mp.zustand?.spieler ?? [];
   const laeuft = mpAktiv && mp.zustand.gestartet && !mp.zustand.gewonnen;

   if (mp.fehler) {
      return <div className="mg-stats text-danger">Keine Verbindung zum Spielserver: {mp.fehler}</div>;
   }
   if (!mp.verbunden) {
      return <div className="mg-stats">Verbinde mit dem Spielserver …</div>;
   }

   return (
      <div className="mg-stats" style={{ flexWrap: "wrap" }}>
         <span>Neue Runde:</span>
         {import.meta.env.DEV && (
            <Button variant="outline-dark" disabled={laeuft} onClick={() => onStart(4)}>
               Test
            </Button>
         )}
         <Button variant="outline-dark" disabled={laeuft} onClick={() => onStart(9)}>
            9 Paare
         </Button>
         <Button variant="outline-dark" disabled={laeuft} onClick={() => onStart(16)}>
            16 Paare
         </Button>
          <Button variant="outline-dark" disabled={laeuft} onClick={() => onStart(31)}>
            31 Paare
         </Button>
         <Button variant="outline-dark" disabled={laeuft} onClick={() => onStart(mitarbeiterAnzahl - 1)}>
            Alle
         </Button>
         <Button variant="outline-secondary" onClick={mp.zuruecksetzen}>
            Abbrechen
         </Button>

         <span className="mg-versuche">
            Versuche: <strong>{versuche}</strong>
         </span>
         <span className="mg-fortschritt">
            {geloest} / {anzahl} Paare
         </span>

         <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
            {spieler.map((s) => {
               const istAmZug = mp.zustand?.amZug === s.name;
               return (
                  <Badge
                     key={s.name}
                     bg={istAmZug ? "success" : "secondary"}
                     style={{ opacity: s.online ? 1 : 0.45 }}
                     title={s.online ? "" : "gerade nicht verbunden"}
                  >
                     {s.name === eigenerName ? `${s.name} (du)` : s.name}: {s.punkte}
                  </Badge>
               );
            })}
            {spieler.length === 0 && <em>Noch niemand dabei</em>}
         </span>

         {mpAktiv && mp.zustand.gestartet && !mp.zustand.gewonnen && (
            <strong style={{ color: binAmZug ? "#2f6f4f" : undefined }}>
               {binAmZug ? "Du bist am Zug" : `${mp.zustand.amZug} ist am Zug`}
            </strong>
         )}
      </div>
   );
}

function Karte({ card, aufgedeckt, gelöst, photoBaseUrl, onClick, hardcore, gesperrt = false }) {
   return (
      <button
         className={`mg-card ${aufgedeckt ? "is-flipped" : ""} ${gelöst ? "is-solved" : ""}`}
         onClick={onClick}
         disabled={gelöst || gesperrt}
         aria-label={aufgedeckt ? kartenBeschriftung(card) : "Karte verdeckt"}
      >
         <div className="mg-card-inner">
            <div className="mg-card-face mg-card-back">
               <img
                  src={`${photoBaseUrl}Mitarbeiter/backside.jpg`}
                  alt={``}
                  style={{ objectPosition: "center" }}
                  className="mg-foto"
               />
            </div>
            <div
               className="mg-card-face mg-card-front"
               style={{
                  objectPosition: "center",
               }}
            >
               {card.type === "foto" ? (
                  <FotoSeite data={card.data} photoBaseUrl={photoBaseUrl} hardcore={hardcore} />
               ) : (
                  <InfoSeite data={card.data} photoBaseUrl={photoBaseUrl} />
               )}
            </div>
         </div>
      </button>
   );
}

function FotoSeite({ data, photoBaseUrl, hardcore }) {
   const [fehler, setFehler] = useState(false);
   const dateiname = `${encodeURIComponent(data.Vorname.replace(" ", ""))}_${encodeURIComponent(data.Nachname.replace(" ", ""))}.jpg`;
   const src = `${photoBaseUrl}Mitarbeiter/${dateiname}`;

   return (
      <div className="mg-foto-seite">
         <div
            style={{
               position: "absolute",
               color: "#96c11f",
               background: "#0009",
               padding: "2px 6px",
               backdropFilter: "blur(4px)",
               fontSize: 14,
               borderRadius: 6,
               boxShadow: "0 0 10px #fff",
               margin: "0.6rem ",
               right: "0%",
               bottom: "0%",
               fontWeight: 900,
            }}
         >
            {data.Kuerzel}
         </div>
         {!fehler ? (
            <img
               src={src}
               alt={`${data.Vorname} ${data.Nachname}`}
               className="mg-foto"
               style={{
                  ...(hardcore ? { filter: "blur(1px)" } : {}),
               }}
               onError={(err) => {
                  setFehler(true);
               }}
            />
         ) : (
            <div className="mg-foto-fallback" style={{ fontSize: 6, overflow: "show" }}>
               {src}
            </div>
         )}
         {hardcore && (
            <div
               style={{
                  position: "absolute",
                  inset: 0,
                  background: "radial-gradient(ellipse 100% 30% at 50% 25%, transparent 0%,#000e 33%, #000e 100%)",
                  pointerEvents: "none",
               }}
            />
         )}
      </div>
   );
}

function InfoSeite({ data, photoBaseUrl }) {
   return (
      <div
         className="mg-info-seite"
         style={{
            backgroundImage: `linear-gradient(#96c11f99, #96c11faa), url(${photoBaseUrl}Standorte/${encodeURIComponent(data.standort)}.jpg)`,
         }}
      >
         <div className="mg-info-Kuerzel">{data.Kuerzel}</div>
         <div className="mg-info-name">
            {data.Vorname} {data.Nachname}
         </div>
         {/*  <div className="mg-info-position">{data.abteilung}</div>
         <div className="mg-info-standort" style={{ wordBreak: "break-all" }}>
            {data.description}
         </div>

         <div className="mg-info-standort">📍 {data.standort}</div> */}
      </div>
   );
}
function kartenBeschriftung(card) {
   return card.type === "foto"
      ? `Foto von ${card.data.Vorname} ${card.data.Nachname}`
      : `Info: ${card.data.Vorname} ${card.data.Nachname}, ${card.data.standort}`;
}
